/**
 * @file api.ts
 * @brief TanStack Query hooks for Super Admin Reports.
 *
 * @details
 * Revenue-by-product and company-health hit additive `/api/admin/reports/*`
 * endpoints. Cost-margin reuses existing analytics revenue hooks.
 *
 * @references
 * - shared/api/endpoints.ts (adminEndpoints.reports*)
 * - features/analytics/api.ts (cost-margin composition)
 *
 * @author DES
 * @date 27-07-2026
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiGet } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import {
  BillingAnomaliesReportSchema,
  CompanyHealthSchema,
  EnterprisePipelineReportSchema,
  QualityOpsReportSchema,
  RevenueByProductSchema,
  type BillingAnomaliesReport,
  type CompanyHealthRow,
  type EnterprisePipelineReport,
  type QualityOpsReport,
  type ReportRange,
  type RevenueByProductRow,
} from "./schemas";

/**
 * The viewer's IANA zone, sent with every windowed query so the backend groups
 * day/week/month buckets on the SAME calendar the window was cut on. Without
 * it the server truncates on its own zone (UTC) and a bucket can fall outside
 * the requested range — the defect found in the invoices daily-spend feed on
 * 24-08-2026. Omitting it keeps the old UTC behaviour.
 */
function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function rangeQuery(range: ReportRange): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(range)) {
    if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
  }
  usp.set("tz", viewerTimeZone());
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const adminReportsKeys = {
  all: ["admin", "reports"] as const,
  revenueByProduct: (range: ReportRange) =>
    ["admin", "reports", "revenue-by-product", range] as const,
  companyHealth: (range: ReportRange) =>
    ["admin", "reports", "company-health", range] as const,
  billingAnomalies: (range: ReportRange) =>
    ["admin", "reports", "billing-anomalies", range] as const,
  enterprisePipeline: (range: ReportRange) =>
    ["admin", "reports", "enterprise-pipeline", range] as const,
  qualityOps: (range: ReportRange) =>
    ["admin", "reports", "quality-ops", range] as const,
};

export function useRevenueByProductReport(
  range: ReportRange,
  options?: Partial<UseQueryOptions<RevenueByProductRow[]>>,
) {
  return useQuery<RevenueByProductRow[]>({
    queryKey: adminReportsKeys.revenueByProduct(range),
    queryFn: async () => {
      const url = `${adminEndpoints.reportsRevenueByProduct}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return RevenueByProductSchema.parse(raw);
    },
    ...options,
  });
}

export function useCompanyHealthReport(
  range: ReportRange,
  options?: Partial<UseQueryOptions<CompanyHealthRow[]>>,
) {
  return useQuery<CompanyHealthRow[]>({
    queryKey: adminReportsKeys.companyHealth(range),
    queryFn: async () => {
      const url = `${adminEndpoints.reportsCompanyHealth}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return CompanyHealthSchema.parse(raw);
    },
    ...options,
  });
}

export function useBillingAnomaliesReport(
  range: ReportRange,
  options?: Partial<UseQueryOptions<BillingAnomaliesReport>>,
) {
  return useQuery<BillingAnomaliesReport>({
    queryKey: adminReportsKeys.billingAnomalies(range),
    queryFn: async () => {
      const url = `${adminEndpoints.reportsBillingAnomalies}${rangeQuery(range)}`;
      const raw = await apiGet<unknown>(url);
      return BillingAnomaliesReportSchema.parse(raw);
    },
    ...options,
  });
}

export function useEnterprisePipelineReport(
  range: ReportRange,
  options?: Partial<UseQueryOptions<EnterprisePipelineReport>>,
) {
  return useQuery<EnterprisePipelineReport>({
    queryKey: adminReportsKeys.enterprisePipeline(range),
    queryFn: async () => {
      const url = `${adminEndpoints.reportsEnterprisePipeline}${rangeQuery(range)}`;
      return EnterprisePipelineReportSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useQualityOpsReport(
  range: ReportRange,
  options?: Partial<UseQueryOptions<QualityOpsReport>>,
) {
  return useQuery<QualityOpsReport>({
    queryKey: adminReportsKeys.qualityOps(range),
    queryFn: async () => {
      const url = `${adminEndpoints.reportsQualityOps}${rangeQuery(range)}`;
      return QualityOpsReportSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}
